import React, {useEffect, useState} from "react";
import * as Yup from "yup";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import useIsMounted from "../../../hooks/use-is-mounted";
import {deepCopy, deepEqual} from "../../../../core/services/utils/utils";
import Api from "../../../../core/services/api/api";
import Dialog from "../../base/dialog";
import {Col, Row} from "reactstrap";
import Divider from "@mui/material/Divider";
import Form from "../../base/form";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import {OutlinedDropdownForm} from "../../base/dropdown/outlined_dropdown";
import {CheckboxForm} from "../../base/checkbox/checkbox";
import Button from "../../base/button/button";
import {ReactComponent as AutoDefineIcon} from "../../../../assets/images/order-notes/auto-define.svg";


const schema = Yup.object().shape({
    subject: Yup.string().trim().nullable().required(ValidateMessages.required),
    body: Yup.string().trim().nullable().required(ValidateMessages.required),
    type: Yup.object().nullable(),
    printPlace: Yup.object().nullable().required(ValidateMessages.required),
    isAutoDefine: Yup.boolean().nullable(),
});

const UpsertOrderNoteDialog = ({
                                   open,
                                   setOpen,
                                   initialValues,
                                   onAdd,
                                   onUpdate,
                                   type,
                                   orderNo,
                               }) => {
    const [loading, setLoading] = useState(false);
    const validate = makeValidate(schema);
    const required = makeRequired(schema);
    const isMounted = useIsMounted();
    const [types, setTypes] = useState([]);
    const [printedPlaces, setPrintedPlaces] = useState([]);

    /**
     * Listens for the changes in open state and with each change:
     * if dialog is open the fetches the raw data needed for the form.
     */
    useEffect(() => {
        if (!open) {
            return
        }
        setLoading(false)
        getTypes()
        getPrintedPlaces()
    }, [open])

    /**
     * Fetches all the available note types from the server and populates the inner list.
     */
    const getTypes = () => {
        Api.getOrderNoteTypes().then((response) => {
            if (!isMounted()) return
            setTypes(response?.data ?? [])
        })
    }

    /**
     * Fetches all the available note printed places from the server and populates the inner list.
     */
    const getPrintedPlaces = () => {
        Api.getOrderNotePrintPlaces().then((response) => {
            if (!isMounted()) return
            setPrintedPlaces(response?.data ?? [])
        })
    }

    /**
     * Creates / Edits the address when the user submits the form. Appends the user id and removes the country of
     * the form values.
     * @param _values {any} the form values.
     */
    const onSubmit = (_values) => {
        let values = deepCopy(_values)
        values = {
            ...values,
            printPlaceId: values?.printPlace?.id,
            noteTypeId: values?.type?.id,
            orderNo: orderNo,
        }
        delete values['printPlace']
        delete values['type']
        if (initialValues != null) {
            values['id'] = initialValues.id
            updateNote(values)
        } else {
            createNote(values);
        }
        return false
    }

    /**
     * Creates a new note with the provided information in the server. if the result is successful, appends the note to
     * the list of notes.
     *
     * @param {any} forApi the note to be created.
     */
    const createNote = (forApi) => {
        setLoading(true);
        Api.createOrderNote(forApi).then((response) => {
            if (!isMounted()) return
            if (response?.resultFlag) {
                setOpen(false);
                onAdd(response?.data);
            }
            setLoading(false);
        })
    }

    /**
     * Updates an existing note with the provided information in the server. if the result is successful, updates the
     * note in the internal list.
     *
     * @param {any} forApi the note to be updated.
     */
    const updateNote = (forApi) => {
        setLoading(true);
        Api.updateOrderNote(forApi).then((response) => {
            if (!isMounted()) return
            if (response?.resultFlag) {
                setOpen(false);
                onUpdate({...response.data, key: initialValues.key});
            }
            setLoading(false);
        })
    }

    /**
     * If autoDefined is set to true, then sets the printedPlace to be equal to the given type.
     * @param e
     * @param form
     */
    const onAutoDefinedChange = (e, form) => {
        form.change(e.target.name, e.target.checked);
        if (!e.target.checked) return
        const _type = type === "Sales order" ? "Sales Order" : type
        form.change('printPlace', printedPlaces?.find(e => e.name === _type))
    }

    const colClass = 'px-3 mb-4';
    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            maxWidth={'sm'}
            loading={loading}
        >
            <Row className='no-gutters align-items-center w-100 transition'>
                <Row xs={12} className='no-gutters justify-content-center m-3 w-100'>
                    <p className='text-lg text-color-1 font-weight-semi-bold text-center'>
                        {initialValues !== null
                            ? 'Edit Note'
                            : 'Create Note'
                        }
                    </p>
                </Row>
                <Divider className='divider'/>
                <div className='my-3 w-100'/>
                <Form
                    className='w-100'
                    onSubmit={onSubmit}
                    validate={validate}
                    initialValues={{
                        ...initialValues,
                        isAutoDefine: initialValues?.isAutoDefine ?? false,
                        subject: initialValues?.subject ?? '',
                        body: initialValues?.body ?? '',
                    }}
                    render={({initialValues: formInitialValues, values, form}) => {
                        return (
                            <Row className='no-gutters justify-content-around w-100'>
                                <Row className='no-gutters justify-content-around w-100'>
                                    <Col xs={12} md={6} className={colClass}>
                                        <OutlinedInputForm
                                            label={'Title'}

                                            name={'subject'}
                                            type={'text'}
                                            required={required.subject}
                                            value={values?.subject ?? ''}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                        />
                                    </Col>
                                    <Col xs={12} md={6} className={colClass}>
                                        <OutlinedDropdownForm
                                            label={'Category (optional)'}
                                            name={`type`}
                                            required={!!required.type}
                                            renderValue={(val) => val?.type ?? ''}
                                            data={types.map(e => ({
                                                value: e,
                                                label: e.type,
                                                id: e.id
                                            })) ?? []}
                                        />
                                    </Col>
                                    <Col xs={8} className={colClass}>
                                        <OutlinedDropdownForm
                                            label={'Show on'}
                                            name={`printPlace`}
                                            disabled={values?.isAutoDefine}
                                            required={required.printPlace}
                                            data={printedPlaces.map(e => ({
                                                value: e,
                                                label: e.name,
                                                id: e.id
                                            })) ?? []}
                                        />
                                    </Col>
                                    <Col xs={4} className={colClass}>
                                        <CheckboxForm
                                            name={'isAutoDefine'}
                                            required={required.isAutoDefine}
                                            onChange={(e) => onAutoDefinedChange(e, form)}
                                            data={{
                                                value: values?.isAutoDefine ?? false,
                                                label: <div className={'d-flex align-items-center'}>
                                                    <AutoDefineIcon className={'mr-1 auto-define'}/>
                                                    <p className={'font-weight-semi-bold text-sm'}>Auto Define</p>
                                                </div>
                                            }}
                                        />
                                    </Col>
                                    <Col xs={12} className={colClass}>
                                        <OutlinedInputForm
                                            label={'Note'}
                                            name={'body'}
                                            placeholder={'The body of the note'}
                                            type={'text'}
                                            multiline
                                            required={required.body}
                                            value={values?.body ?? ''}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                        />
                                    </Col>
                                </Row>
                                <div className='my-3 w-100'/>
                                <Divider className='divider'/>
                                <Row className='no-gutters justify-content-between align-items-start w-100'>
                                    <Button
                                        disabled={!open || loading}
                                        variant={'dialog'}
                                        color={'primary'}
                                        onClick={() => setOpen(false)}
                                        borderRadius={'0 8px 0 8px'}
                                    >
                                        {loading ? 'loading...' : 'Close'}
                                    </Button>
                                    <Button
                                        disabled={
                                            !open ||
                                            loading ||
                                            (initialValues !== null && deepEqual(formInitialValues, values))
                                        }
                                        variant={'dialog'}
                                        color={'primary'}
                                        type={'submit'}
                                        borderRadius={'8px 0 8px 0'}
                                    >
                                        {loading ? 'loading...' : initialValues !== null ? 'Update' : 'Create'}
                                    </Button>
                                </Row>
                            </Row>
                        );
                    }}
                />
            </Row>
        </Dialog>
    );
}

export default UpsertOrderNoteDialog;
